// lib
import PropTypes from 'prop-types'

import { isExpandableGroup } from '@aytm/app/stats/page/sidebar/data-quality/vector-check'
import { runAgainVectorId } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'

// components
import { Button } from 'primereact/button'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/data-quality/styles/DataCentrifugeSection.module.scss'

// User-facing copy for vectors that have no run row of their own — surfaced in the popup
// so the user understands WHY the count is dashed out.
const NA_MESSAGE = 'N/A'

const formatSeconds = seconds => {
  const secondsInt = Math.round(seconds)
  const h = Math.floor(secondsInt / 3600)
  const m = Math.floor((secondsInt - h * 3600) / 60)
  const s = secondsInt - h * 3600 - m * 60

  return `${0 < h ? `${h}:` : ''}${10 > m ? `0${m}` : m}:${10 > s ? `0${s}` : s}`
}

const DataCentrifugeTotal = ({ root, tier, vector, vectorStatus, interactive, runEnabled = true, onFetch }) => {
  // Popup on leaf vectors AND on any flat-rendered parent. Real expandable groups aggregate
  // their children and have no single status of their own — drilldown happens at the child
  // level. The shape must be read with the tier: below the subvector tier a multi-leaf parent
  // renders flat and has no child rows to drill into, so it needs its own popup (sidebar spec
  // §4.0.1 / §4.7).
  const expandable = isExpandableGroup(vector, tier)

  // Which way the popup opens is the shared popup's to decide, from the height its content is drawn
  // at, so this cell hands it no direction of its own.
  return root && vectorStatus && !expandable ? (
    <SidebarPopup
      root={root}
      help={
        <div data-testid={`stat-data-quality-panel-dc-popup-${vector.id}`} className={style['vector-popup']}>
          <div
            data-testid={`stat-data-quality-panel-dc-popup-title-${vector.id}`}
            className={style['vector-popup-title']}
          >
            <svg width="24" height="24" viewBox="0 0 100 100">
              <path
                d={vector.icon}
                fill={'success' != vectorStatus.status.toLowerCase() && vector.disabled ? '#223d64' : vector.color}
              />
            </svg>
            <span>
              <b>{vector.text}</b> vector
            </span>
          </div>
          <div className={style['vector-popup-content']}>
            {/* Started/Ran-for only render when the run actually populated those fields —
                placeholder NA statuses never have them. */}
            {Object.hasOwn(vectorStatus, 'section_start') ? (
              <div className={style['vector-popup-content-row']}>
                <b className={style['vector-popup-content-row-name']}>Started:</b>
                <span data-testid={`stat-data-quality-panel-dc-popup-started-${vector.id}`}>
                  {vectorStatus.section_start}
                </span>
              </div>
            ) : null}
            {Object.hasOwn(vectorStatus, 'duration_sec') ? (
              <div className={style['vector-popup-content-row']}>
                <b className={style['vector-popup-content-row-name']}>Ran for:</b>
                <span data-testid={`stat-data-quality-panel-dc-popup-duration-${vector.id}`}>
                  {formatSeconds(vectorStatus.duration_sec)}
                </span>
              </div>
            ) : null}
            <div className={style['vector-popup-content-row']}>
              <b className={style['vector-popup-content-row-name']}>Status:</b>
              <span
                data-testid={`stat-data-quality-panel-dc-popup-status-${vector.id}`}
                className={style[`vector-popup-content-${vectorStatus.status.toLowerCase()}`]}
              >
                {'na' == vectorStatus.status.toLowerCase()
                  ? NA_MESSAGE
                  : 'string' === typeof vectorStatus.message && vectorStatus.message.trim()
                    ? vectorStatus.message
                    : vectorStatus.status.toLowerCase()}
              </span>
            </div>
            {'success' == vectorStatus.status.toLowerCase() && !vector.total ? (
              <div className={style['vector-popup-content-row']}>
                <span>Vector ran successfully &mdash; no flagged responses found.</span>
              </div>
            ) : null}
            {/* Re-running recomputes the quarantine set, so it is admin-only alongside vector
                selection (CAPABILITY.SELECT_VECTORS), and additionally requires the run feature —
                that tier is wider than the roles a run resolves on for. Either way the failed
                status still shows; only the action is withheld. */}
            {'error' == vectorStatus.status.toLowerCase() && interactive && runEnabled ? (
              <div className={style['vector-popup-content-footer']}>
                <Button
                  data-testid={`stat-data-quality-panel-dc-popup-run-again-${vector.id}`}
                  className="p-button-rounded p-button-blue"
                  label="RUN AGAIN"
                  onClick={() => onFetch([runAgainVectorId(vector.id)])}
                />
              </div>
            ) : null}
          </div>
        </div>
      }
      width="300px"
      trigger="hover"
    >
      <div
        data-testid={`stat-data-quality-panel-dc-total-${vector.id}`}
        className={`${style['vector-total']}${
          !vector.checked || vector.disabled ? ' ' + style['vector-total-disabled'] : ''
        } p-right`}
      >
        {vector.disabled ? <>&ndash;</> : vector.total}
      </div>
    </SidebarPopup>
  ) : (
    <div
      data-testid={`stat-data-quality-panel-dc-total-${vector.id}`}
      className={`${style['vector-total']}${vector.disabled ? ' p-disabled' : ''} p-right`}
    >
      {vector.disabled ? <>&ndash;</> : vector.total}
    </div>
  )
}

DataCentrifugeTotal.propTypes = {
  root: PropTypes.any,
  tier: PropTypes.string,
  vector: PropTypes.object,
  vectorStatus: PropTypes.object,
  interactive: PropTypes.bool,
  runEnabled: PropTypes.bool,
  onFetch: PropTypes.func
}

DataCentrifugeTotal.displayName = 'DataCentrifugeTotal'

export default DataCentrifugeTotal
